import * as React from 'react'
import autobind from 'autobind-decorator'
import {observable, action, makeObservable} from 'mobx'
import { t } from 'ttag'
import {FieldState, FormState} from 'formstate'
import {Airport} from '../Forms/AirportAutocomplete'
import {Airline} from '../Forms/AirlineAutocomplete'
import {
    required,
    validateEmail,
    validateFlightNumber,
    validateFullName,
    validatePhoneNumber,
    validateOnlyTrueCheckbox,
    validateCancellationNotification,
    validateArrivalDelay
} from '../Forms/validation'
import {Upload} from '../Forms/Upload'
import {formatDepartureTime} from '../Forms/conversion'
import {
    trackClaimRegistered,
    trackCovid19WarningTriggered,
    trackIneligibleFlightEntered
} from '../../analytics'
import { getLocale } from '../../locales'
import {StatedCause} from './FlightDetailsForm'
import scrollIntoView from 'scroll-into-view-if-needed'


let HelloSign

try {
    HelloSign = require('hellosign-embedded')
} catch (e) {
    console.error(e)
}


enum DocumentType {
    PASSPORT = 'passport',
    ID_CARD_FRONT = 'id_card_front',
    ID_CARD_BACK = 'id_card_back',
    RIGHT_TO_FLY = 'right_to_fly',
}

interface DocumentUpload {
    type: DocumentType
    upload_id: string
}

export const REQUIRE_SUBMITTER_ADDRESS = false

export class RefFieldState<TValue> extends FieldState<TValue> {
    positionRef = React.createRef()

    scrollIntoView () {
        scrollIntoView(
            this.positionRef.current as any,
            {behavior: 'smooth', block: 'start', inline: 'start'}
        )
    }
}

type IDDocumentValue = {
    idDocSide: RefFieldState<Upload>,
}

export type IDDocumentFormState = FormState<IDDocumentValue>


type PassengerTValue = {
    name: RefFieldState<string>,
    email: RefFieldState<string>,
    phonePrefix: RefFieldState<string>,
    phoneNumber: RefFieldState<string>,
    bookingNumber: RefFieldState<string>,
    boardingPassDoc: RefFieldState<Upload>,
    idDoc: FormState<IDDocumentFormState[]>,
    isPassportSelected: RefFieldState<boolean>,
    isChild: RefFieldState<boolean>,
}

export type PassengerFormState = FormState<PassengerTValue>


type FlightValue = {
    departure_airport: RefFieldState<Airport>,
    destination_airport: RefFieldState<Airport>,
    departure_date: RefFieldState<Date>,
    airline: RefFieldState<Airline>,
    flight_number: RefFieldState<string>,
}

export type FlightFormState = FormState<FlightValue>


export class ClaimFormState {
    @observable hasExtraPassengers = false
    @observable isDirectFlight = true
    @observable useEmbeddedSignature = true
    @observable formKey = 0
    @observable globalErrorMessage : string = null
    @observable lastClaimNumber : string = null
    @observable lastClaimEmail : string = null
    @observable submitting = false
    @observable isClaimable = true
    @observable amountPerPassenger = 0
    @observable partialClaimNumber = null

    covid19WarningTracked = false
    ineligibleFlightTracked = false

    @observable formState = this.initFormState()

    constructor() {
        makeObservable(this)
    }

    @action
    initFormState () {
        // Departure airport is first element, destination airport is last element,
        // connecting airports in the middle.
        let route = new FormState([
            this.makeAirportFieldState(),
            this.makeAirportFieldState(),
        ])

        let flights = new FormState({
            flights: new FormState<FlightFormState[]>([]),
            flight_issue: new RefFieldState<string>(null).validators(
                required(t`Please select the issue with the flight`)
            ),
            flight_cancellation_notification: new RefFieldState(null).validators(
                validateCancellationNotification(this)
            ),
            arrival_delay: new RefFieldState(null).validators(
                validateArrivalDelay(this)
            ),
            problem_flight_position: new FieldState<number>(null).validators(
                required(t`Please select a flight`)
            ),
            stated_cause: new FieldState<string>(null).validators(
                required(t`Please select the explanation given by the airline`)
            ),
        })

        // Submitter contact data is shown on a separate screen so here we also split
        // it of into a separate form state, to allow separately validating only the part that
        // is currently visible.
        let submitterContact = new FormState({
            name: new RefFieldState<string>(null).validators(
                required(t`Please enter your full name`),
                validateFullName
            ),
            email: new RefFieldState<string>(null).validators(
                required(t`Please enter your email address`),
                validateEmail
            ),
            phonePrefix: new RefFieldState<string>(null),
            phoneNumber: new RefFieldState<string>(null).validators(validatePhoneNumber),
            address1: new RefFieldState<string>(null).validators(
                REQUIRE_SUBMITTER_ADDRESS ? required(t`Please enter your address`) : x => null,
            ),
            address2: new RefFieldState<string>(null),
            city: new RefFieldState<string>(null).validators(
                REQUIRE_SUBMITTER_ADDRESS ? required(t`Please enter your city name`) : x => null,
            ),
            postal_code: new RefFieldState<string>(null).validators(
                REQUIRE_SUBMITTER_ADDRESS ? required(t`Please enter your postal code`) : x => null,
            ),
            state: new RefFieldState<string>(null),
            country: new RefFieldState<string>(null).validators(
                REQUIRE_SUBMITTER_ADDRESS ? required(t`Please enter your country name`) : x => null,
            ),
            allowProcessPersonalData: new RefFieldState<boolean>(false).validators(
                validateOnlyTrueCheckbox
            ),
        })

        let submitterItinerary = new FormState({
            bookingNumber: new RefFieldState<string>(null).validators(
                required(t`Please enter your booking number`)
            ),
            boardingPassDoc: new RefFieldState<Upload>(null).validators(
                required(t`Please upload your boarding pass`)
            ),
            idDoc: new FormState<IDDocumentFormState[]>([this.makeIDDocumentFormState()]),
            isPassportSelected: new RefFieldState<boolean>(true).validators(),
            isChild: new RefFieldState<boolean>(false).validators(),
        })

        let submitter = new FormState({
            name: submitterContact.$.name,
            email: submitterContact.$.email,
            phonePrefix: submitterContact.$.phonePrefix,
            phoneNumber: submitterContact.$.phoneNumber,
            bookingNumber: submitterItinerary.$.bookingNumber,
            boardingPassDoc: submitterItinerary.$.boardingPassDoc,
            idDoc: submitterItinerary.$.idDoc,
            isPassportSelected: submitterItinerary.$.isPassportSelected,
            isChild: submitterItinerary.$.isChild,
        })

        let submitterAddress = new FormState({
            address1: submitterContact.$.address1,
            address2: submitterContact.$.address2,
            city: submitterContact.$.city,
            postal_code: submitterContact.$.postal_code,
            state: submitterContact.$.state,
            country: submitterContact.$.country,
        })

        let passenger = new FormState<PassengerFormState[]>(
            [this.makePassengerFormState(false)]
        )

        return {
            route,
            flights,
            submitterContact,
            submitterItinerary,
            submitterAddress,
            submitter,
            passenger,
        }
    }

    clearForm () {
        this.formState = this.initFormState()
    }

    @action
    resetForm () {
        this.clearForm()
        this.isDirectFlight = true
        this.hasExtraPassengers = false

        this.lastClaimNumber = null
        this.lastClaimEmail = null
        this.formKey += 1
    }

    claimSubmitted (claimNumber) {
        this.lastClaimNumber = claimNumber
    }

    makeAirportFieldState () {
        let pageState = this
        let fieldState = new RefFieldState<Airport>(null)

        function validateRouteAirportUnique (airport: Airport) {
            let airportFields = pageState.formState.route.$
            let checkFields = []
            let fieldIdx = airportFields.indexOf(fieldState)

            if (airport == null) {
                return null
            }

            // Don't validate uniqueness for starting airport
            if (fieldIdx == 0) {
                return null
            }

            // Validate final destination airport only against the departure
            // airport, since visually that's the only airport before it.
            if (fieldIdx == airportFields.length - 1) {
                checkFields = [airportFields[0]]
            } else {
                checkFields = airportFields.slice(0, fieldIdx)
                // Add destination airport to the list of airport to check against,
                // since visually it is before any connecting airports
                checkFields.splice(0, 0, airportFields[airportFields.length-1])
            }

            let seenAirportIds = []
            for (let checkField of checkFields) {
                if (checkField.$) {
                    seenAirportIds.push(checkField.$.id)
                }
            }

            if (seenAirportIds.includes(airport.id)) {
                return t`Airports must not repeat`
            }
        }

        function validateRouteEndpointIsEU (airport: Airport) {
            let airportFields = pageState.formState.route.$
            let fieldIdx = airportFields.indexOf(fieldState)

            if (airport == null) {
                return null
            }

            // Don't validate connecting airports, only endpoints
            if (fieldIdx != 0 && fieldIdx != (airportFields.length - 1)) {
                return null
            }

            let depAirportField = airportFields[0]
            let arrAirportField = airportFields[airportFields.length - 1]

            let isDepartureAirportEU = !depAirportField.value || depAirportField.value.is_eu
            let isArrivalAirportEU = !arrAirportField.value || arrAirportField.value.is_eu

            if (!isDepartureAirportEU && !isArrivalAirportEU) {
                pageState.onIneligibleFlight()
                return t`Compensation can not be claimed when both airports are outside the EU`
            }
        }

        fieldState.validators(
            required(t`Please select an airport`),
            validateRouteAirportUnique,
            validateRouteEndpointIsEU
        )

        return fieldState
    }

    makePassengerFormState (requireEmail: boolean = true) : PassengerFormState {
        let emailValidators = [validateEmail]

        if (requireEmail) {
            emailValidators.unshift(required(t`Please enter the email address of the passenger`))
        }

        return new FormState({
            name: new RefFieldState<string>(null).validators(
                required(t`Please enter the full name of the passenger`),
                validateFullName
            ),
            email: new RefFieldState<string>(null).validators(...emailValidators),
            bookingNumber: new RefFieldState<string>(null),
            phonePrefix: new RefFieldState<string>(null),
            phoneNumber: new RefFieldState<string>(null).validators(validatePhoneNumber),
            boardingPassDoc: new RefFieldState<Upload>(null).validators(
                required(t`Please upload the boarding pass for this passenger`)
            ),
            idDoc: new FormState<IDDocumentFormState[]>([this.makeIDDocumentFormState()]),
            isPassportSelected: new RefFieldState<boolean>(true).validators(),
            isChild: new RefFieldState<boolean>(false).validators(),
        })
    }

    @autobind
    addExtraPassenger () {
        this.formState.passenger.$.push(this.makePassengerFormState(false))
    }

    removeExtraPassenger (index) {
        this.formState.passenger.$.splice(index, 1)
    }
    
    makeFlightFormState () : FlightFormState {
        let pageState = this
        let flightNumber = new RefFieldState<string>(null)

        function validateFlightNumberUnique (flightNumber: string) {
            if (flightNumber == null) {
                return null
            }

            let flightNos = []
            pageState.formState.flights.$.flights.$.map((flight) => {
                flightNos.push(flight.$.flight_number.value)
            })


            if (flightNos.filter(item => item == flightNumber).length > 1) {
                return t`Flight numbers must not repeat`
            }
        }

        return new FormState({
            departure_airport: new RefFieldState<Airport>(null).validators(
                required(t`Please select the departure airport`)
            ),
            destination_airport: new RefFieldState<Airport>(null).validators(
                required(t`Please select the destination airport`)
            ),
            departure_date: new RefFieldState<Date>(null).validators(
                required(t`Please select the scheduled departure date`)
            ),
            airline: new RefFieldState<Airline>(null).validators(
                required(t`Please select the airline for the flight`)
            ),
            flight_number: flightNumber.validators(
                required(t`Please enter the flight number`),
                validateFlightNumberUnique,
                validateFlightNumber
            ),
        })
    }

    @autobind
    addConnectingAirport () {
        let airports = this.formState.route.$
        airports.splice(airports.length - 1, 0, this.makeAirportFieldState())
    }

    @autobind
    removeConnectingAirport (index) {
        this.formState.route.$.splice(index, 1)
    }


    @autobind
    onConnectingAirportCountChange ({ target }: React.ChangeEvent<HTMLInputElement>) {
        const airportCount = this.formState.route.$.length
        const connectingAirportCount = airportCount - 2
        const newConnectingAirportCount = Number(target.value)
        const isAirportRemoving = newConnectingAirportCount < connectingAirportCount
        const airportCountDifference = Math.abs(newConnectingAirportCount - connectingAirportCount)

        if (airportCountDifference) {
            for (let i = 0; i < airportCountDifference; i++) {
                isAirportRemoving
                    ? this.formState.route.$.splice(this.formState.route.$.length - 2, 1)
                    : this.formState.route.$.splice(this.formState.route.$.length - 1, 0, this.makeAirportFieldState())
            }
        }
    }

    initFlights () {
        // Clear existing flights
        this.formState.flights.$.flights.$ = []

        // Generate flights from airports
        for (let i = 0; i < this.formState.route.$.length - 1; i++) {
            let flightForm = this.makeFlightFormState()

            this.formState.flights.$.flights.$.push(flightForm)

            flightForm.$.departure_airport.reset(this.formState.route.$[i].$)
            flightForm.$.destination_airport.reset(this.formState.route.$[i+1].$)
        }

        // Preselect the affected flight index if there is only one flight
        if (this.formState.flights.$.flights.$.length == 1) {
            this.formState.flights.$.problem_flight_position.reset(0)
        } else {
            this.formState.flights.$.problem_flight_position.reset(null)
        }
    }

    makeIDDocumentFormState () : IDDocumentFormState {
        return new FormState({
            idDocSide: new RefFieldState<Upload>(null).validators(
                required(t`Please select the ID document`)
            ),
        })
    }

    @autobind
    async submitForm () {
        let flightFormResult = await this.formState.flights.validate()

        if (flightFormResult.hasError) {
            this.globalErrorMessage = t`Invalid flight data entered`
            return false
        }

        let submitterFormResult = await this.formState.submitter.validate()
        if (submitterFormResult.hasError) {
            this.globalErrorMessage = t`Invalid submitter data entered`
            return false
        }

        if (this.hasExtraPassengers) {
            let passengerFormResult = await this.formState.passenger.validate()
            if (passengerFormResult.hasError) {
                this.globalErrorMessage = t`Invalid passenger data entered`
                return false
            }
        }

        this.submitting = true
        let response = await fetch(
            '/api/cases',
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    ...this.serializeForm(),
                }),
            }
        )

        if (!response.ok) {
            try {
                this.globalErrorMessage = (await response.json()).message
            } catch (e) {
                this.globalErrorMessage = response.statusText
            }

            this.submitting = false
            return false
        }

        let responseData = await response.json()

        this.lastClaimNumber = responseData.case_number
        this.lastClaimEmail = this.formState.submitter.$.email.value

        if (responseData.order_id) {
            let firstFlight = this.formState.flights.$.flights.$[0]
            let lastFlight = this.formState.flights.$.flights.$[
                this.formState.flights.$.flights.$.length-1
            ]

            let departureAirport = firstFlight.$.departure_airport.value
            let destinationAirport = lastFlight.$.destination_airport.value
            let passengerCount = 1
            
            if (this.hasExtraPassengers) {
                passengerCount += this.formState.passenger.$.length
            }

            let totalCompensationAmount = this.amountPerPassenger * passengerCount
        }

        trackClaimRegistered()

        if (responseData.sign_url) {
            let helloSign = new HelloSign({
                skipDomainVerification: location.hostname === 'localhost',
                clientId: process.env.HELLOSIGN_CLIENT_ID,
                allowCancel: false,
            })
    
            helloSign.on('ready', async () => {
                this.submitting = false
            })
            helloSign.on('sign', async () => {
                await fetch(`/api/hellosign-return?claim=${responseData.case_number}`)
            })

            helloSign.open(responseData.sign_url)
        }
        

        return true
    }

    private serializeForm() {
        let flights = []
        for (let f of this.formState.flights.$.flights.$) {
            let dd = f.$.departure_date.value.getDate()
            let mm = f.$.departure_date.value.getMonth()+1
            let yyyy = f.$.departure_date.value.getFullYear()

            let fd = {
                departure_airport: f.$.departure_airport.value.id,
                arrival_airport: f.$.destination_airport.value.id,
                departure_date: yyyy + '-' + (mm > 9 ? '' : '0') + mm + '-' + (dd > 9 ? '' : '0') + dd,
                airline: f.$.airline.value.id,
                flight_number: f.$.flight_number.value,
            }

            flights.push(fd)
        }

        let ffs = this.formState.flights.$

        if (ffs.problem_flight_position.value !== null) {
            flights[ffs.problem_flight_position.value].arrival_delay =
                ffs.arrival_delay.value * 3600 // Arrival delay in seconds
        }

        let phone_number = null
        if (this.formState.submitter.$.phoneNumber.value) {
            phone_number = (
                this.formState.submitter.$.phonePrefix.value
                + this.formState.submitter.$.phoneNumber.value
            )
        }

        let paxForms = [this.formState.submitter]
        if (this.hasExtraPassengers) {
            paxForms = paxForms.concat(this.formState.passenger.$)
        }

        let data = {
            departure_time: formatDepartureTime(ffs.flights.$[0].$.departure_date.value),
            flight_issue: ffs.flight_issue.value,
            stated_cause: ffs.stated_cause.value,
            booking_number: this.formState.submitterItinerary.$.bookingNumber.value,
            flight_cancellation_notification: ffs.flight_cancellation_notification.value,
            name: this.formState.submitter.$.name.value,
            email: this.formState.submitter.$.email.value,
            phone_number: phone_number,
            sign_now: this.useEmbeddedSignature,
            problem_flight_position: ffs.problem_flight_position.value,
            passengers: paxForms.map((pf, index) => this.serializePassengerForm(pf, index)),
            language: getLocale(location).shortCode,
            partial_claim_number: this.partialClaimNumber,
            flights,
        }

        if (data['stated_cause'] == StatedCause.NO_EXPLANATION) {
            data['stated_cause'] = null
        }

        if (!data['comment'] || data['comment'].trim() == '') {
            data['comment'] = null
        }

        if (!data['phone_number'] || data['phone_number'].trim() == '') {
            data['phone_number'] = null
        }

        return data
    }




    @autobind
    async submitPartialForm () {
        let flightFormResult = await this.formState.flights.validate()

        if (flightFormResult.hasError) {
            this.globalErrorMessage = t`Invalid flight data entered`
            return false
        }

        this.submitting = true
        let response = await fetch(
            '/api/partial-claims',
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    ...this.serializePartialForm(),
                }),
            }
        )
        this.submitting = false

        if (!response.ok) {
            try {
                this.globalErrorMessage = (await response.json()).message
            } catch (e) {
                this.globalErrorMessage = response.statusText
            }

            return false
        }

        let responseData = await response.json()

        this.partialClaimNumber = responseData.case_number

        return true
    }

    private serializePartialForm() {
        let flights = []
        for (let f of this.formState.flights.$.flights.$) {
            let dd = f.$.departure_date.value.getDate()
            let mm = f.$.departure_date.value.getMonth()+1
            let yyyy = f.$.departure_date.value.getFullYear()

            let fd = {
                departure_airport: f.$.departure_airport.value.id,
                arrival_airport: f.$.destination_airport.value.id,
                departure_date: yyyy + '-' + (mm > 9 ? '' : '0') + mm + '-' + (dd > 9 ? '' : '0') + dd,
                airline: f.$.airline.value.id,
                flight_number: f.$.flight_number.value,
            }

            flights.push(fd)
        }

        let ffs = this.formState.flights.$

        if (ffs.problem_flight_position.value !== null) {
            flights[ffs.problem_flight_position.value].arrival_delay =
                ffs.arrival_delay.value * 3600 // Arrival delay in seconds
        }

        let phone_number = null
        if (this.formState.submitter.$.phoneNumber.value) {
            phone_number = this.formState.submitter.$.phonePrefix.value + this.formState.submitter.$.phoneNumber.value
        }

        let paxForms = [this.formState.submitter]
        
        const sa = this.formState.submitterAddress
        const address = {
            address1: sa.$.address1.value,
            address2: sa.$.address2.value || null,
            city: sa.$.city.value,
            postal_code: sa.$.postal_code.value,
            state: sa.$.state.value || null,
            country: sa.$.country.value,
        }

        let data = {
            departure_time: formatDepartureTime(ffs.flights.$[0].$.departure_date.value),
            flight_issue: ffs.flight_issue.value,
            stated_cause: ffs.stated_cause.value,
            booking_number: this.formState.submitterItinerary.$.bookingNumber.value,
            flight_cancellation_notification: ffs.flight_cancellation_notification.value,
            name: this.formState.submitter.$.name.value,
            email: this.formState.submitter.$.email.value,
            phone_number: phone_number,
            problem_flight_position: ffs.problem_flight_position.value,
            passengers: paxForms.map((pf, index) =>  {
                return {
                    name: pf.$.name.value,
                    email: pf.$.email.value,
                    phone_number: pf.$.phoneNumber.value ? pf.$.phonePrefix.value + pf.$.phoneNumber.value : null,
                    documents: [],
                    ...(index === 0 && REQUIRE_SUBMITTER_ADDRESS ? { address } : {}),
                    is_child: pf.$.isChild.value,
                }
            }),
            language: getLocale(location).shortCode,
            flights,
        }

        if (data['stated_cause'] == StatedCause.NO_EXPLANATION) {
            data['stated_cause'] = null
        }

        if (!data['comment'] || data['comment'].trim() == '') {
            data['comment'] = null
        }

        if (!data['phone_number'] || data['phone_number'].trim() == '') {
            data['phone_number'] = null
        }

        return data
    }




    private serializePassengerForm(p: PassengerFormState, index: number) {
        let documents: DocumentUpload[] = []

        documents.push(
            {type: DocumentType.RIGHT_TO_FLY, upload_id: p.$.boardingPassDoc.value.id}
        )

        if (p.$.isPassportSelected.value) {
            documents.push(
                {type: DocumentType.PASSPORT, upload_id: p.$.idDoc.$[0].$.idDocSide.value.id}
            )
        } else {
            documents.push(
                {type: DocumentType.ID_CARD_FRONT, upload_id: p.$.idDoc.$[0].$.idDocSide.value.id}
            )
            documents.push(
                {type: DocumentType.ID_CARD_BACK, upload_id: p.$.idDoc.$[1].$.idDocSide.value.id}
            )
        }

        const sa = this.formState.submitterAddress
        const address = {
            address1: sa.$.address1.value,
            address2: sa.$.address2.value || null,
            city: sa.$.city.value,
            postal_code: sa.$.postal_code.value,
            state: sa.$.state.value || null,
            country: sa.$.country.value,
        }

        let pd = {
            name: p.$.name.value,
            email: p.$.email.value,
            phone_number: p.$.phoneNumber.value ? p.$.phonePrefix.value + p.$.phoneNumber.value : null,
            documents,
            ...(index === 0 && REQUIRE_SUBMITTER_ADDRESS ? { address } : {}),
            is_child: p.$.isChild.value,
        }

        if (!(pd.email || '').trim()) pd.email = null
        if (!(pd.phone_number || '').trim()) pd.phone_number = null

        return pd
    }

    @autobind
    onIsDirectFlightChange(value: boolean) {
        if (value == this.isDirectFlight) return

        let airports = this.formState.route.$

        if (value) {
            airports.splice(1, airports.length - 2)
        } else {
            // Assuming only departure and destination airports currently in array
            this.addConnectingAirport()
        }

        this.isDirectFlight = value
    }

    @autobind
    onShowCovid19Warning () {
        if (!this.covid19WarningTracked) {
            this.covid19WarningTracked = true
            trackCovid19WarningTriggered()
        }
    }

    @autobind
    onIneligibleFlight () {
        if (this.ineligibleFlightTracked) return

        this.ineligibleFlightTracked = true
        trackIneligibleFlightEntered()
    }
}


let stateInst:ClaimFormState = null

export function getStateInstance () : ClaimFormState {
    if (!stateInst) {
        stateInst = new ClaimFormState()
    }

    return stateInst
}
